import { useState } from 'react';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import LabeledInput from '@/components/atoms/LabeledInput';
import Button from '@/components/atoms/Button/Button';
import Icon from '@/components/atoms/Icon';

// Static copy describing what a duplicate carries over versus what it leaves behind.
// A duplicate copies only the recording structure and timeline edits — never the
// project's derived assets. Kept here so the checklist stays in one place.
const INCLUDED_ITEMS = [
  'Timeline edits & cuts',
  'Scene layout & speaker arrangement',
  'Captions & transcript edits',
  'Applied effects & transitions',
  'Music tracks',
  'Image/Video Overlays'
];

const EXCLUDED_ITEMS = ['Chapters'];

interface DuplicateRecordingModalProps {
  isOpen: boolean;
  defaultName: string;
  isCreating: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
}

export default function DuplicateRecordingModal({
  isOpen,
  defaultName,
  isCreating,
  onClose,
  onConfirm
}: DuplicateRecordingModalProps) {
  const [name, setName] = useState(defaultName);

  const trimmedName = name.trim();
  const canSubmit = !!trimmedName && !isCreating;

  const handleConfirm = () => {
    if (!canSubmit) {
      return;
    }
    onConfirm(trimmedName);
  };

  return (
    <ContentDialog
      isOpen={isOpen}
      setIsOpen={value => !value && onClose()}
      size="small"
      hideTitle
      disableBackdropClose={isCreating}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <Icon name="IconCopy" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-slate-900">New Duplicate</h2>
            <p className="mt-0.5 text-sm text-slate-500">Creates a copy of the Original recording</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isCreating}
            aria-label="Close"
            className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon name="IconX" size={16} stroke={1.9} />
          </button>
        </div>

        {/* Info banner */}
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-blue-50 p-3">
          <span className="mt-0.5 shrink-0 text-blue-600">
            <Icon name="IconInfoCircle" size={16} />
          </span>
          <p className="text-sm font-semibold text-blue-700">
            The project and it’s assets will not be duplicated - only the full recording video and any edits performed
            on the recording will be duplicated.
          </p>
        </div>

        {/* What gets duplicated */}
        <p className="mt-5 text-xs font-medium text-slate-400">What gets duplicated</p>
        <ul className="mt-2 space-y-2.5">
          {INCLUDED_ITEMS.map(item => (
            <li key={item} className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <span className="shrink-0 text-green-500">
                <Icon name="IconCheck" size={18} />
              </span>
              {item}
            </li>
          ))}
          {EXCLUDED_ITEMS.map(item => (
            <li key={item} className="flex items-center gap-2 text-sm font-medium text-slate-400">
              <span className="shrink-0 text-red-400">
                <Icon name="IconX" size={18} />
              </span>
              {item}
            </li>
          ))}
        </ul>

        {/* Name input */}
        <div className="mt-5">
          <LabeledInput
            label="New recording name"
            value={name}
            onChange={setName}
            autoFocus
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                handleConfirm();
              }
            }}
          />
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variation="outline" onClick={onClose} disabled={isCreating} trackingId="cancel-duplicate-button">
            Cancel
          </Button>
          <Button
            variation="filled"
            onClick={handleConfirm}
            disabled={!canSubmit}
            trackingId="add-duplicate-button"
            className="gap-1.5"
          >
            <Icon name="IconPlus" size={14} />
            {isCreating ? 'Adding...' : 'Add Duplicate'}
          </Button>
        </div>
      </div>
    </ContentDialog>
  );
}
